// src/components/Swimming/SwimmingDashboard.jsx
import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, LineElement, PointElement, LinearScale,
  Title, Tooltip, Legend, CategoryScale,
} from 'chart.js';
import { calculateSwimmingPace } from '../../utils/paceSwimmingUtils';
import { calculateLapSwolf } from '../../utils/swolfUtils';
import { useTranslation } from '../../i18n';

ChartJS.register(LineElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

const SwimmingDashboard = ({ laps }) => {
  const { t } = useTranslation();

  if (!laps || laps.length === 0) return null;

  const labels = laps.map((_, index) => `Lap ${index + 1}`);

  const paceData     = laps.map((lap) =>
    calculateSwimmingPace(lap.total_distance, lap.total_timer_time)
      ? parseFloat(((lap.total_timer_time / lap.total_distance) * 100).toFixed(2))
      : null
  );
  const strokesData  = laps.map((l) => l.total_cycles   ?? null);
  const swolfData    = laps.map((l) => calculateLapSwolf(l));
  const hrData       = laps.map((l) => l.avg_heart_rate ?? null);
  const distanceData = laps.map((l) => l.total_distance ?? null);

  const datasets = [];

  if (paceData.some((v) => v !== null))
    datasets.push({ label: t('swimming.paceLabel'),    data: paceData,     borderColor: '#36A2EB', backgroundColor: '#36A2EB', yAxisID: 'y1' });
  if (strokesData.some((v) => v !== null))
    datasets.push({ label: t('swimming.strokesLabel'), data: strokesData,  borderColor: '#FFCE56', backgroundColor: '#FFCE56', yAxisID: 'y2' });
  if (hrData.some((v) => v !== null))
    datasets.push({ label: t('swimming.hrLabel'),      data: hrData,       borderColor: '#FF6384', backgroundColor: '#FF6384', yAxisID: 'y2' });
  if (distanceData.some((v) => v !== null))
    datasets.push({ label: t('swimming.distanceLabel'),data: distanceData, borderColor: '#f7941d', backgroundColor: '#f7941d', yAxisID: 'y2' });
  if (swolfData.some((v) => v !== null))
    datasets.push({ label: t('swimming.swolfLabel'),   data: swolfData,    borderColor: '#4BC0C0', backgroundColor: '#4BC0C0', yAxisID: 'y3' });

  const paceLabel = t('swimming.paceLabel');

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y1: { type: 'linear', position: 'left',  title: { display: true, text: t('swimming.paceAxisLabel') } },
      y2: { type: 'linear', position: 'right', title: { display: true, text: t('swimming.rightAxisLabel') }, grid: { drawOnChartArea: false } },
      y3: { type: 'linear', position: 'left', offset: true, title: { display: true, text: t('swimming.swolfAxisLabel') }, grid: { drawOnChartArea: false } },
    },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: t('swimming.dashboardTitle'), font: { size: 18, weight: 'bold' }, padding: { top: 10, bottom: 20 } },
      tooltip: {
        mode: 'index', intersect: false,
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || '';
            const value = context.raw;
            if (datasetLabel === paceLabel && value) {
              const minutes = Math.floor(value / 60);
              const seconds = Math.round(value % 60);
              return `${datasetLabel}: ${minutes}:${seconds < 10 ? '0' : ''}${seconds} /100m`;
            }
            return `${datasetLabel}: ${value}`;
          }
        }
      }
    },
  };

  const baseLapWidth = 120;
  const lapThreshold = 5;
  const chartWidth   = laps.length <= lapThreshold ? '100%' : `${laps.length * baseLapWidth}px`;

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ width: chartWidth, minWidth: '600px', height: '400px', margin: '0 auto' }}>
        <Line data={{ labels, datasets }} options={chartOptions} />
      </div>
    </div>
  );
};

export default SwimmingDashboard;
